import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useRunAiFeature } from "@/hooks/useRunAiFeature"
import { AiFeatureItem, AiInputButtons } from "@/modules/chats/components/chat-input/ai-input-buttons"
import { buildChatCaseSummaryTrackingMetadata, useChatCaseSummary } from "@/modules/chats/hooks/useChatCaseSummary"
import { useAppendChatHistoryMessage, useReadChatHistory } from "@/modules/chats/hooks/useChatHistory"
import { nextBestQuestionsQuery } from "@/modules/chats/services/ai/nextBestQuestionsQuery"
import { questionnaireSummarizerQuery } from "@/modules/chats/services/ai/questionnaireSummarizerQuery"
import { useChatInputContext, useChatInputStateGetter } from "@/modules/chats/stores/useChatInputStore"
import { AiTrackingMetadata } from "@/modules/chats/types/chat-message"
import { ChatThread, getAutoTranslationTargetLanguage } from "@/modules/chats/types/chat-thread"

type ChatAiInputProps = { selectedThread: ChatThread }

export const ChatAiInput = ({ selectedThread }: ChatAiInputProps) => {
  const { t } = useTranslation()

  const isAiRunning = useChatInputContext((store) => store.aiRunning !== "NONE")
  const isSendingMessage = useChatInputContext((store) => store.isSendingMessage)

  const getChatInputState = useChatInputStateGetter()
  const appendHistory = useAppendChatHistoryMessage()
  const readChatHistory = useReadChatHistory()
  const runAiFeature = useRunAiFeature()
  const targetAgentLanguage = getAutoTranslationTargetLanguage(selectedThread)

  const handleNextBestQuestions = useCallback(
    () =>
      runAiFeature({
        context: "CHAT",
        start: () => getChatInputState().setAiRunning("NEXT_BEST_QUESTIONS"),
        stop: () => getChatInputState().setAiRunning("NONE"),
        faro: { event: "aiFeature_nextBestQuestions", feature: "Next best questions" },
        run: (aiFeatureId) =>
          nextBestQuestionsQuery(selectedThread.patient.country, {
            aiFeatureId,
            appointmentId: selectedThread.appointmentId,
            age: selectedThread.patient.age.years,
            gender: selectedThread.patient.gender,
            targetAgentLanguage,
          }),
        selectMeta: (response) => response.nextBestQuestions,
        onSuccess: (meta) => getChatInputState().setNextBestQuestions([...meta.questions].map((text) => text)),
        onError: (error) => console.error("Best Question request is failed", error),
      }),
    [selectedThread, targetAgentLanguage, getChatInputState, runAiFeature],
  )

  const handleSummarizeQuestionnaire = useCallback(
    () =>
      runAiFeature({
        context: "CHAT",
        start: () => getChatInputState().setAiRunning("QUESTIONNAIRE_SUMMARIZER"),
        stop: () => getChatInputState().setAiRunning("NONE"),
        faro: { event: "aiFeature_questionnaire_summarizer_chat", feature: "Summarize questionnaire for Chat" },
        run: (aiFeatureId) => {
          if (!selectedThread.questionnaireId || !selectedThread.appointmentId) {
            return Promise.resolve(null)
          }
          return questionnaireSummarizerQuery(selectedThread.patient.country, {
            flavour: "CHAT",
            aiRequestMetadata: {
              aiFeatureId,
              appointmentId: selectedThread.appointmentId,
              age: selectedThread.patient.age.years,
              gender: selectedThread.patient.gender,
              targetAgentLanguage,
            },
          })
        },
        selectMeta: (response) => response.summarizeQuestionnaire,
        onSuccess: (meta, aiFeatureId) => {
          const { setAiTrackingMetadata } = getChatInputState()

          const currentText = readChatHistory().at(-1)?.content ?? ""
          const newMessage = (currentText ? currentText + "\n" : "") + meta.text

          appendHistory(newMessage, "QUESTIONNAIRE_SUMMARIZER_CHAT")

          const trackingMetadata: AiTrackingMetadata = {
            id: aiFeatureId,
            name: "Summarize questionnaire for Chat",
            previousText: currentText,
            newText: newMessage,
            aiFeature: "QUESTIONNAIRE_SUMMARIZER_CHAT",
            fileHash: meta.fileHash,
            aiRequestDurationMs: meta.aiRequestDurationMs,
            aiAnonymizationRequestDurationMs: meta.aiAnonymizationRequestDurationMs,
            createdAt: new Date().toISOString(),
          }
          setAiTrackingMetadata([trackingMetadata])
        },
        onError: () => {
          const { messageText, setMessageText } = getChatInputState()
          setMessageText(messageText)
        },
      }),
    [selectedThread, targetAgentLanguage, getChatInputState, appendHistory, readChatHistory, runAiFeature],
  )

  const runChatCaseSummary = useChatCaseSummary(selectedThread)

  const handleChatForConfirmation = useCallback(async () => {
    const result = await runChatCaseSummary()
    if (!result) {
      return
    }

    const previousText = readChatHistory().at(-1)?.content ?? ""
    appendHistory(result.text, "CHAT_CASE_SUMMARY_CONFIRMATION_MESSAGE", previousText)
    getChatInputState().setAiTrackingMetadata([buildChatCaseSummaryTrackingMetadata(result, previousText)])
  }, [runChatCaseSummary, readChatHistory, appendHistory, getChatInputState])

  const aiFeatureItems = useMemo<AiFeatureItem[]>(
    () => [
      {
        type: "NEXT_BEST_QUESTIONS",
        name: t("chat.aiFeature.nextBestQuestions.name"),
        description: t("chat.aiFeature.nextBestQuestions.description"),
        action: handleNextBestQuestions,
        shouldShow: true,
      },
      {
        type: "QUESTIONNAIRE_SUMMARIZER",
        name: t("chat.aiFeature.questionnaireSummarizer.name"),
        description: t("chat.aiFeature.chatForConfirmationSummarizer.description"),
        action: handleSummarizeQuestionnaire,
        shouldShow: !!selectedThread.questionnaireId,
      },
      {
        type: "CHAT_CASE_SUMMARY_CONFIRMATION",
        name: t("chat.aiFeature.chatForConfirmationSummarizer.name"),
        description: t("chat.aiFeature.chatForConfirmationSummarizer.description"),
        action: handleChatForConfirmation,
        shouldShow: true,
      },
    ],
    [
      t,
      selectedThread.questionnaireId,
      handleNextBestQuestions,
      handleSummarizeQuestionnaire,
      handleChatForConfirmation,
    ],
  )

  return <AiInputButtons items={aiFeatureItems} isRunning={isAiRunning || isSendingMessage} />
}
